import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createBulletinBoards,
  deleteBulletinBoards,
  getAllBulletinBoards,
} from "../../services/BulletinBoard/BulletinBoard";
import { getAllCompaniesService } from "../../services/Company/CompanyServices";
import { deletePosts, getAllPosts, updatePosts } from "../../services/Post/Post";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface NamedRef {
  _id: string;
  name: string;
}

interface AdminPost {
  _id: string;
  idCompany: NamedRef;
  title: string;
  dateTime: string;
  description: string;
  images: any[];
  comments: string[];
}

const AdminPosts = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [pinnedPostIds, setPinnedPostIds] = useState<Set<string>>(new Set());
  const [companies, setCompanies] = useState<NamedRef[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [editingPost, setEditingPost] = useState<AdminPost | null>(null);
  const [deletingPost, setDeletingPost] = useState<AdminPost | null>(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([getAllPosts(), getAllBulletinBoards(), getAllCompaniesService()])
      .then(([postsRes, bulletinBoardsRes, companiesRes]: any[]) => {
        setPosts(postsRes.data ?? []);
        setPinnedPostIds(
          new Set((bulletinBoardsRes.data ?? []).map((b: any) => b._idPost))
        );
        setCompanies(companiesRes.data ?? []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredPosts = useMemo(() => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return posts;
    return posts.filter(
      (post) =>
        post.title?.toLowerCase().includes(search) ||
        post.description?.toLowerCase().includes(search) ||
        post.idCompany?.name?.toLowerCase().includes(search)
    );
  }, [posts, globalFilterValue]);

  const togglePinned = (post: AdminPost, pinned: boolean) => {
    const action = pinned
      ? createBulletinBoards({ _idPost: post._id })
      : deleteBulletinBoards({ _idPost: post._id });
    action
      .then(() => {
        setPinnedPostIds((prev) => {
          const next = new Set(prev);
          pinned ? next.add(post._id) : next.delete(post._id);
          return next;
        });
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const submitEdit = () => {
    if (!editingPost) return;
    updatePosts({
      _id: editingPost._id,
      idCompany: editingPost.idCompany._id,
      title: editingPost.title,
      dateTime: editingPost.dateTime,
      description: editingPost.description,
      images: editingPost.images ?? [],
    })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingPost(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingPost) return;
    deletePosts({ _id: deletingPost._id })
      .then(() => {
        setPosts((prev) => prev.filter((p) => p._id !== deletingPost._id));
        setDeletingPost(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:posts")} />

      <div className="admin-page-body">
      <div className="p-input-icon-left mb-3">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => setGlobalFilterValue(e.target.value)}
          placeholder="Keyword Search"
        />
      </div>

      <DataTable
        value={filteredPosts}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        sortField="dateTime"
        sortOrder={-1}
        emptyMessage={t("common:noResults")}
      >
        <Column field="title" header={t("common:Name")} sortable />
        <Column
          header={t("common:companies")}
          body={(row: AdminPost) => row.idCompany?.name}
        />
        <Column
          field="dateTime"
          header={t("common:date")}
          body={(row: AdminPost) => new Date(row.dateTime).toLocaleDateString()}
          sortable
        />
        <Column
          header={t("common:comments")}
          body={(row: AdminPost) => row.comments?.length ?? 0}
        />
        <Column
          header={t("common:pinned")}
          body={(row: AdminPost) => (
            <Checkbox
              checked={pinnedPostIds.has(row._id)}
              onChange={(e) => togglePinned(row, !!e.checked)}
            />
          )}
        />
        <Column
          body={(row: AdminPost) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingPost({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingPost(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={editingPost?.title}
        visible={editingPost != null}
        onHide={() => setEditingPost(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingPost(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingPost && (
          <div className="p-fluid">
            <div className="field">
              <label>{t("common:Name")}</label>
              <InputText
                value={editingPost.title}
                onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
              />
            </div>
            <div className="field">
              <label>{t("common:Description")}</label>
              <InputTextarea
                rows={4}
                value={editingPost.description}
                onChange={(e) =>
                  setEditingPost({ ...editingPost, description: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:date")}</label>
              <Calendar
                value={new Date(editingPost.dateTime)}
                onChange={(e) =>
                  setEditingPost({
                    ...editingPost,
                    dateTime: (e.value as Date)?.toISOString() ?? editingPost.dateTime,
                  })
                }
                showTime
              />
            </div>
            <div className="field">
              <label>{t("common:companies")}</label>
              <Dropdown
                value={editingPost.idCompany?._id}
                options={companies.map((c) => ({ value: c._id, label: c.name }))}
                onChange={(e) =>
                  setEditingPost({
                    ...editingPost,
                    idCompany: companies.find((c) => c._id === e.value) as NamedRef,
                  })
                }
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingPost(null)}
        visible={deletingPost != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingPost(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingPost?.title}
      </Dialog>
    </div>
  );
};

export default AdminPosts;
